import React, { useState, useMemo } from 'react';
import {
    FaCheckCircle, FaExclamationTriangle, FaClock,
    FaSyncAlt, FaChevronDown, FaChevronUp,
    FaUserFriends, FaLayerGroup, FaUsers, FaInfoCircle,
    FaReceipt, FaExclamationCircle, FaCheck, FaPercentage,
    FaCreditCard, FaMobileAlt, FaUniversity, FaExternalLinkAlt,
    FaTimes, FaCalendarAlt
} from 'react-icons/fa';
import './PaymentCard.css';

// ── Inscription alert ─────────────────────────────────────────────────────────
const InscriptionAlert = ({ inscriptionInfo }) => {
    const unpaid = inscriptionInfo?.filter(i => !i.paid && i.feeDiscount > 0);
    if (!unpaid || unpaid.length === 0) return null;

    return (
        <div className="payment-inscription-alert">
            <div className="alert-icon-box">
                <FaExclamationTriangle />
            </div>
            <div className="alert-content">
                <strong className="alert-title">Taxa de Inscrição Pendente</strong>
                <ul className="alert-list">
                    {unpaid.map(i => (
                        <li key={`alert-${i.athleteTeamId}`}>
                            {i.sportName}{i.escalao ? ` — ${i.escalao}` : ''}: <strong className="alert-amount">{i.feeDiscount.toFixed(2)} €</strong>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
};

// ── Breakdown list ────────────────────────────────────────────────────────────
const BreakdownList = ({ breakdown }) => {
    if (!breakdown || breakdown.length === 0) return null;

    return (
        <div className="payment-breakdown">
            {breakdown.map((item, i) => (
                <div
                    key={i}
                    className={`payment-breakdown-item ${item.isDiscount ? 'is-discount' : ''} ${item.quotaIncluded ? 'quota-included' : ''}`}
                >
                    <span className="breakdown-label">
                        {item.quotaIncluded && <span className="breakdown-dot breakdown-dot--included" title="Quota incluída" />}
                        {item.isDiscount && <span className="breakdown-dot breakdown-dot--discount" title="Desconto" />}
                        {!item.quotaIncluded && !item.isDiscount && <span className="breakdown-dot breakdown-dot--normal" />}
                        {item.label}
                    </span>
                    <span className="breakdown-amount">
                        {item.isDiscount
                            ? <span className="amount-discount">− {Math.abs(item.amount).toFixed(2)} €</span>
                            : <span>{item.amount.toFixed(2)} €</span>}
                    </span>
                </div>
            ))}
        </div>
    );
};

// ── Main PaymentCard component ────────────────────────────────────────────────
const PaymentCard = ({
    paymentStatus,
    quotaAmount,
    totalDue,
    overdueMonths,
    futureMonths = [],
    breakdown,
    inscriptionInfo,
    nextPeriod,
    paymentHistory,
    historyInscriptions,
    historyYear,
    onHistoryYearChange,
    onStartPayment,
    startingPayment,
    parentsPaymentWarning,
    isPollingPayment = false,
    paymentPreference = 'Monthly',
    discountsApplied = [],
}) => {
    const [showBreakdown, setShowBreakdown] = useState(false);
    const [isOverdueDropdownOpen, setIsOverdueDropdownOpen] = useState(false);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const [showExplanation, setShowExplanation] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [selectedMonthKeys, setSelectedMonthKeys] = useState(new Set());

    // Build the full list of payable months
    // Order: overdue (oldest first) → current calendar month → future (up to 11 months ahead)
    const allPayableMonths = useMemo(() => {
        const now = new Date();
        const realCurrentMonth = now.getMonth() + 1;
        const realCurrentYear = now.getFullYear();

        const months = [
            ...(overdueMonths || []).map(m => ({
                key: `${m.periodMonth ?? 'annual'}-${m.periodYear}`,
                periodMonth: m.periodMonth,
                periodYear: m.periodYear,
                amount: m.amount || 0,
                monthlyQuota: m.monthlyQuota,
                breakdown: m.breakdown,
                inscriptionInfo: m.inscriptionInfo,
                isCurrent: false,
                isFuture: false,
            }))
        ];

        const historyList = Array.isArray(paymentHistory) ? paymentHistory : (paymentHistory?.payments || []);

        const isCurrentMonthPaid = paymentStatus === 'Regularizada' || historyList.some(
            p => p.periodYear === realCurrentYear && p.periodMonth === realCurrentMonth && (p.displayStatus === 'Completed' || p.status === 'Completed' || p.status === 'succeeded')
        );

        // Add current calendar month if monthly and not already paid
        if (paymentPreference !== 'Annual' && !isCurrentMonthPaid) {
            const alreadyIn = months.some(
                m => m.periodMonth === realCurrentMonth && m.periodYear === realCurrentYear
            );
            if (!alreadyIn) {
                const currentUnpaidInsc = (inscriptionInfo || []).filter(i => {
                    if (i.paid || !i.feeDiscount || i.feeDiscount <= 0) return false;
                    const isBadminton = (i.sportName || '').toLowerCase().includes('badminton');
                    return isBadminton ? (realCurrentMonth >= 1 && realCurrentMonth < 9) : (realCurrentMonth >= 9);
                });
                const currentInscAmt = currentUnpaidInsc.reduce((s, i) => s + (i.feeDiscount || 0), 0);
                const currentMonthAmt = (quotaAmount || 0) + currentInscAmt;

                months.push({
                    key: `current-${realCurrentMonth}-${realCurrentYear}`,
                    periodMonth: realCurrentMonth,
                    periodYear: realCurrentYear,
                    amount: currentMonthAmt,
                    monthlyQuota: quotaAmount || 0,
                    inscriptionsAmount: currentInscAmt,
                    inscriptions: currentUnpaidInsc.map(i => ({ sportName: i.sportName, amount: i.feeDiscount })),
                    breakdown: breakdown || [],
                    inscriptionInfo: inscriptionInfo || [],
                    isCurrent: true,
                    isFuture: false,
                });
            }
        }

        // Add next 11 future months with their specific calculated amount
        if (paymentPreference !== 'Annual') {
            if (futureMonths && futureMonths.length > 0) {
                futureMonths.forEach(fm => {
                    const isPaidInHistory = historyList.some(
                        p => p.periodMonth === fm.periodMonth && p.periodYear === fm.periodYear && (p.displayStatus === 'Completed' || p.status === 'Completed' || p.status === 'succeeded')
                    );
                    const alreadyIn = months.some(
                        m => m.periodMonth === fm.periodMonth && m.periodYear === fm.periodYear
                    );
                    if (!alreadyIn && !isPaidInHistory) {
                        months.push({
                            key: `future-${fm.periodMonth}-${fm.periodYear}`,
                            periodMonth: fm.periodMonth,
                            periodYear: fm.periodYear,
                            amount: fm.amount || 0,
                            monthlyQuota: fm.monthlyQuota,
                            inscriptionsAmount: fm.inscriptionsAmount || 0,
                            inscriptions: fm.inscriptions || [],
                            breakdown: fm.breakdown,
                            inscriptionInfo: fm.inscriptionInfo,
                            isCurrent: false,
                            isFuture: true,
                        });
                    }
                });
            } else if ((quotaAmount || 0) > 0) {
                for (let i = 1; i <= 11; i++) {
                    const d = new Date();
                    d.setMonth(d.getMonth() + i);
                    const fMonth = d.getMonth() + 1;
                    const fYear = d.getFullYear();
                    const isPaidInHistory = historyList.some(
                        p => p.periodMonth === fMonth && p.periodYear === fYear && (p.displayStatus === 'Completed' || p.status === 'Completed' || p.status === 'succeeded')
                    );
                    const alreadyIn = months.some(
                        m => m.periodMonth === fMonth && m.periodYear === fYear
                    );
                    if (!alreadyIn && !isPaidInHistory) {
                        months.push({
                            key: `future-${fMonth}-${fYear}`,
                            periodMonth: fMonth,
                            periodYear: fYear,
                            amount: quotaAmount || 0,
                            monthlyQuota: quotaAmount || 0,
                            inscriptionsAmount: 0,
                            inscriptions: [],
                            breakdown: breakdown || [],
                            inscriptionInfo: [],
                            isCurrent: false,
                            isFuture: true,
                        });
                    }
                }
            }
        }

        return months;
    }, [overdueMonths, futureMonths, quotaAmount, totalDue, paymentPreference, paymentStatus, paymentHistory, inscriptionInfo, breakdown]);

    const openPaymentModal = () => {
        // Pre-select overdue + current months only (NOT future months)
        const preSelected = allPayableMonths
            .filter(m => !m.isFuture)
            .map(m => m.key);
        setSelectedMonthKeys(new Set(preSelected));
        setShowPaymentModal(true);
    };

    const toggleMonthSelection = (key) => {
        setSelectedMonthKeys(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const selectedTotal = allPayableMonths
        .filter(m => selectedMonthKeys.has(m.key))
        .reduce((sum, m) => sum + m.amount, 0);

    const handleConfirmPayment = () => {
        const selectedOverdue = (overdueMonths || []).filter(m =>
            selectedMonthKeys.has(`${m.periodMonth ?? 'annual'}-${m.periodYear}`)
        );
        const currentMonthItem = allPayableMonths.find(m => m.isCurrent);
        const includesCurrentMonth = currentMonthItem ? selectedMonthKeys.has(currentMonthItem.key) : false;
        const selectedFuture = allPayableMonths
            .filter(m => m.isFuture && selectedMonthKeys.has(m.key))
            .map(m => ({
                periodMonth: m.periodMonth,
                periodYear: m.periodYear,
                amount: m.amount,
                monthlyQuota: m.monthlyQuota,
                breakdown: m.breakdown,
                inscriptionInfo: m.inscriptionInfo,
                inscriptions: m.inscriptions,
                inscriptionsAmount: m.inscriptionsAmount
            }));
        setShowPaymentModal(false);
        onStartPayment(selectedOverdue, includesCurrentMonth, selectedTotal, selectedFuture, currentMonthItem);
    };

    const getExplanationPoints = () => {
        const points = [];

        // 1. Overdue Months
        if (overdueMonths && overdueMonths.length > 0) {
            const overdueTotal = overdueMonths.reduce((s, m) => s + m.amount, 0);
            points.push({
                type: 'warning',
                text: `Valor em atraso: Acumulou ${overdueMonths.length} ${overdueMonths.length === 1 ? 'mês anterior' : 'meses anteriores'} de quotas/mensalidades em falta, totalizando ${overdueTotal.toFixed(2)} €.`
            });
        }

        // 2. Base Membership fee (Quota de Sócio)
        const hasParentExemption = discountsApplied?.includes('parent_member_exemption') ||
            breakdown?.some(item => item.label?.toLowerCase().includes('isenta') && item.isDiscount);

        const hasQuotaIncluded = breakdown?.some(item => item.quotaIncluded);

        const memberFeeItem = breakdown?.find(item => item.label?.toLowerCase().includes('quota de sócio') && !item.quotaIncluded);

        if (parentsPaymentWarning) {
            points.push({
                type: 'warning',
                text: 'Aviso de Quota: O desconto/isenção de quota de menor (até aos 12 anos) não pôde ser aplicado porque as quotas de sócio dos pais não estão regularizadas para o mês corrente, aplicando-se o valor padrão.'
            });
        } else if (hasParentExemption) {
            points.push({
                type: 'success',
                text: 'Isenção Família: Está isento do pagamento da quota global de sócio (0,00 €) por ter ambos os pais como sócios ativos e regularizados (aplicável até aos 12 anos).'
            });
        } else if (hasQuotaIncluded) {
            const sportWithQuota = breakdown.find(item => item.quotaIncluded);
            points.push({
                type: 'info',
                text: `Quota de Sócio: A sua quota mensal de sócio está totalmente coberta pela mensalidade da modalidade ${sportWithQuota?.sportName || 'ativa'}.`
            });
        } else if (memberFeeItem) {
            points.push({
                type: 'info',
                text: `Quota de Sócio: Inclui a quota mensal de sócio no valor de ${memberFeeItem.amount.toFixed(2)} €.`
            });
        }

        // 3. Modalities (Mensalidades)
        const sportFees = breakdown?.filter(item => item.sportName && !item.label?.toLowerCase().includes('inscrição'));

        sportFees?.forEach(item => {
            const escalaoStr = item.escalao ? ` — ${item.escalao}` : '';
            if (item.amount === 0 && (item.label?.toLowerCase().includes('defeso') || item.label?.toLowerCase().includes('suspensa'))) {
                points.push({
                    type: 'info',
                    text: `Modalidade: Mensalidade de ${item.sportName}${escalaoStr} está suspensa temporariamente devido ao período de defeso (Julho e Agosto).`
                });
            } else if (item.amount === 0 && item.label?.toLowerCase().includes('isento')) {
                points.push({
                    type: 'info',
                    text: `Modalidade: Isento da mensalidade de ${item.sportName}${escalaoStr}.`
                });
            } else {
                let note = '';
                if (item.quotaIncluded) {
                    note = ' (com quota de sócio integrada)';
                }
                points.push({
                    type: 'info',
                    text: `Modalidade: Custo mensal de ${item.sportName}${escalaoStr} é de ${item.amount.toFixed(2)} €${note}.`
                });
            }
        });

        // 4. Discounts (Descontos)
        if (discountsApplied?.includes('sibling')) {
            points.push({
                type: 'success',
                text: 'Desconto Irmão: Aplicado desconto na mensalidade por ter um irmão federado no clube.'
            });
        }

        if (discountsApplied?.includes('second_sport')) {
            points.push({
                type: 'success',
                text: 'Desconto 2ª Modalidade: Aplicado desconto na modalidade de menor custo.'
            });
        }

        // 5. Inscriptions (Taxas de Inscrição)
        const unpaidInscriptions = inscriptionInfo?.filter(i => !i.paid && i.feeDiscount > 0);
        unpaidInscriptions?.forEach(i => {
            const escalaoStr = i.escalao ? ` — ${i.escalao}` : '';
            points.push({
                type: 'info',
                text: `Inscrição Pendente: Inclui a taxa de inscrição para a época na modalidade ${i.sportName}${escalaoStr} no valor de ${i.feeDiscount.toFixed(2)} €.`
            });
        });

        return points;
    };

    const explanationPoints = getExplanationPoints();

    const MONTHS_PT = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
    const MONTHS_PT_SHORT = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

    const effectiveAmount = totalDue != null ? totalDue : (quotaAmount + (overdueMonths?.reduce((s, m) => s + m.amount, 0) || 0));

    // history logic
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;
    const hasCurrentMonthInHistory = paymentHistory?.some(p => p.periodYear === currentYear && p.periodMonth === currentMonth);

    let mergedHistory = [
        ...(paymentHistory || []).map(p => {
            const isInscription = p.description?.toLowerCase().includes('inscrição');
            return {
                ...p,
                type: isInscription ? 'inscription' : 'quota',
                displayStatus: 'Completed'
            };
        }),
        ...(historyInscriptions || [])
            .filter(i => i.paid && !(paymentHistory || []).some(p => p.description?.toLowerCase().includes('inscrição') && p.description?.toLowerCase().includes(i.sportName?.toLowerCase())))
            .map(i => ({
                ...i,
                type: 'inscription',
                displayStatus: 'Completed',
                description: `Taxa Inscrição — ${i.sportName}`,
                amount: i.amount || 0,
                periodYear: historyYear,
                periodMonth: 9,
                paymentDate: i.paymentDate || new Date().toISOString()
            }))
    ];

    if (!hasCurrentMonthInHistory && historyYear === currentYear) {
        const virtualStatus = paymentStatus === 'Pendente' ? 'Pending' : (paymentStatus === 'Regularizada' ? 'Completed' : 'Unpaid');
        if (virtualStatus !== 'Completed') {
            mergedHistory.push({
                type: 'quota',
                periodMonth: currentMonth,
                periodYear: currentYear,
                displayStatus: virtualStatus,
                amount: quotaAmount || 0,
                paymentDate: new Date().toISOString(),
                description: paymentPreference === 'Annual' ? `Quota Anual ${currentYear}` : `Quota ${MONTHS_PT[currentMonth - 1]} ${currentYear}`
            });
        }
    }

    mergedHistory.sort((a, b) => {
        // 1. Sort by Year descending
        const yearA = a.periodYear ?? (a.paymentDate ? new Date(a.paymentDate).getFullYear() : 0);
        const yearB = b.periodYear ?? (b.paymentDate ? new Date(b.paymentDate).getFullYear() : 0);
        if (yearB !== yearA) return yearB - yearA;

        // 2. Sort by Month descending (inscriptions for the season placed right alongside September)
        const monthA = a.type === 'inscription' ? 9.5 : (a.periodMonth ?? 0);
        const monthB = b.type === 'inscription' ? 9.5 : (b.periodMonth ?? 0);
        if (monthB !== monthA) return monthB - monthA;

        // 3. Sort by paymentDate descending
        const dateA = a.paymentDate ? new Date(a.paymentDate).getTime() : 0;
        const dateB = b.paymentDate ? new Date(b.paymentDate).getTime() : 0;
        return dateB - dateA;
    });

    return (
        <div className="dashboard-card glass-card">
            <div className="card-header">
                <div className="card-title">
                    <div className="card-icon"><FaReceipt /></div>
                    <h2>Estado de Pagamento</h2>
                </div>
            </div>

            <div className="payment-card-body-new">
                {isPollingPayment && (
                    <div className="payment-polling-alert" style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '12px 16px',
                        marginBottom: '16px',
                        background: 'rgba(0, 51, 128, 0.05)',
                        border: '1px solid rgba(0, 51, 128, 0.15)',
                        borderRadius: '12px',
                        color: '#003380',
                        fontSize: '0.9rem',
                        fontWeight: '500'
                    }}>
                        <FaSyncAlt style={{ animation: 'spin-subtle 2s linear infinite' }} />
                        <span>A atualizar estado do pagamento...</span>
                        <style>{`
                            @keyframes spin-subtle {
                                0% { transform: rotate(0deg); }
                                100% { transform: rotate(360deg); }
                            }
                        `}</style>
                    </div>
                )}

                {parentsPaymentWarning && (
                    <div className="payment-parents-warning-alert">
                        <div className="alert-icon-box warning">
                            <FaExclamationCircle />
                        </div>
                        <div className="alert-content">
                            <strong className="alert-title">Aviso de Quota</strong>
                            <p className="alert-text">
                                O desconto de quota (até aos 12 anos) não foi aplicado porque as quotas dos pais não estão regularizadas para o mês atual.
                            </p>
                        </div>
                    </div>
                )}

                <div className="payment-summary-new">
                    <div className="payment-summary-main">
                        <div className="payment-summary-item-new large">
                            <p>Total a Pagar</p>
                            <h3 className={effectiveAmount > 0 ? 'text-danger' : 'text-success'}>
                                {effectiveAmount !== null ? `€${Number(effectiveAmount).toFixed(2)}` : '€0.00'}
                            </h3>
                            <span className="total-due-explanation">
                                {overdueMonths?.length > 0
                                    ? `Quota atual + ${overdueMonths.length} ${overdueMonths.length === 1 ? 'mês em atraso' : 'meses em atraso'}`
                                    : paymentPreference === 'Annual' ? 'Quota anual atual' : 'Quota do mês atual'}
                            </span>
                        </div>
                    </div>

                    {/* Resumo Explicativo do Valor */}
                    {explanationPoints.length > 0 && (
                        <div className="payment-explanation-container">
                            <button
                                className={`explanation-toggle-btn ${showExplanation ? 'active' : ''}`}
                                onClick={() => setShowExplanation(!showExplanation)}
                                type="button"
                            >
                                <span className="toggle-btn-title">
                                    <FaInfoCircle className="info-icon-blue" />
                                    Como é calculado o meu valor?
                                </span>
                                <span className="toggle-btn-chevron">
                                    {showExplanation ? <FaChevronUp /> : <FaChevronDown />}
                                </span>
                            </button>
                            {showExplanation && (
                                <div className="explanation-content-panel">
                                    <div className="explanation-divider"></div>
                                    <ul className="explanation-points-list">
                                        {explanationPoints.map((pt, idx) => (
                                            <li key={idx} className={`explanation-point-item pt-${pt.type}`}>
                                                <span className="point-bullet"></span>
                                                <span className="point-text">{pt.text}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}

                    {showBreakdown && (
                        <div className="payment-breakdown-wrapper">
                            <BreakdownList breakdown={breakdown?.filter(item => item.amount !== 0)} />
                        </div>
                    )}


                </div>

                <div className="payment-status-actions">

                    {onStartPayment && (
                        <div className="payment-action-block">
                            <div className="stripe-payment-methods">
                                <span className="stripe-methods-label">Métodos de pagamento aceites:</span>
                                <div className="stripe-methods-icons">
                                    <span className="stripe-method-badge"><FaCreditCard /> Cartão</span>
                                    <span className="stripe-method-badge"><FaUniversity /> Multibanco</span>
                                    <span className="stripe-method-badge"><FaMobileAlt /> MBWay</span>
                                </div>
                            </div>

                            <button
                                className="payment-btn-new primary stripe-pay-btn"
                                onClick={() => openPaymentModal()}
                                disabled={startingPayment || isPollingPayment}
                            >
                                {startingPayment
                                    ? <><FaSyncAlt className="icon-spin" /> A processar…</>
                                    : isPollingPayment
                                        ? <><FaSyncAlt className="icon-spin" style={{ animation: 'spin-subtle 2s linear infinite' }} /> A atualizar…</>
                                        : effectiveAmount > 0 && paymentStatus !== 'Regularizada'
                                            ? <><FaExternalLinkAlt /> Pagar Quota ({effectiveAmount.toFixed(2)} €)</>
                                            : <><FaExternalLinkAlt /> Pagar / Antecipar Quotas</>}
                            </button>

                            <p className="stripe-secure-note">🔒 Pagamento seguro via Stripe</p>
                        </div>
                    )}

                    {/* ── Partial Month Selection Modal ───────────────────────── */}
                    {showPaymentModal && (
                        <div className="pms-overlay" onClick={() => setShowPaymentModal(false)}>
                            <div className="pms-modal" onClick={e => e.stopPropagation()}>
                                <div className="pms-header">
                                    <div className="pms-header-icon"><FaCalendarAlt /></div>
                                    <div>
                                        <h3 className="pms-title">Selecionar Meses a Pagar</h3>
                                        <p className="pms-subtitle">
                                            {allPayableMonths.length} {allPayableMonths.length === 1 ? 'mês disponível' : 'meses disponíveis'} — escolhe os que queres pagar agora
                                        </p>
                                    </div>
                                    <button className="pms-close" onClick={() => setShowPaymentModal(false)}>
                                        <FaTimes />
                                    </button>
                                </div>

                                <div className="pms-select-actions">
                                    <button
                                        className="pms-select-link"
                                        onClick={() => setSelectedMonthKeys(new Set(allPayableMonths.map(m => m.key)))}
                                    >Selecionar todos</button>
                                    <span className="pms-select-sep">·</span>
                                    <button
                                        className="pms-select-link"
                                        onClick={() => setSelectedMonthKeys(new Set())}
                                    >Limpar seleção</button>
                                </div>

                                <div className="pms-months-list">
                                    {/* ── Overdue + Current months ─────────────── */}
                                    {allPayableMonths.filter(m => !m.isFuture).map((m) => {
                                        const checked = selectedMonthKeys.has(m.key);
                                        const monthLabel = m.periodMonth
                                            ? new Date(m.periodYear, m.periodMonth - 1, 1)
                                                .toLocaleDateString('pt-PT', { month: 'long', year: 'numeric' })
                                            : `Anual ${m.periodYear}`;
                                        const label = monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1);
                                        return (
                                            <label
                                                key={m.key}
                                                className={`pms-month-row ${checked ? 'checked' : ''} ${m.isCurrent ? 'is-current' : 'is-overdue'}`}
                                            >
                                                <div className="pms-checkbox-wrap">
                                                    <input type="checkbox" checked={checked}
                                                        onChange={() => toggleMonthSelection(m.key)}
                                                        className="pms-checkbox" />
                                                    <span className="pms-custom-check">{checked && <FaCheck />}</span>
                                                </div>
                                                <div className="pms-month-info">
                                                    <div className="pms-month-title-row">
                                                        <span className="pms-month-name">{label}</span>
                                                        <div className="pms-badges-wrap">
                                                            {m.isCurrent ? (
                                                                <span className="pms-badge current">Mês atual</span>
                                                            ) : (
                                                                <span className="pms-badge overdue">Em atraso</span>
                                                            )}
                                                            {m.inscriptionsAmount > 0 && (
                                                                <span className="pms-badge inscription">
                                                                    + Inscrição {m.inscriptions?.map(i => i.sportName).join(', ')}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {m.inscriptionsAmount > 0 && (
                                                        <span className="pms-month-subdetail">
                                                            Mensalidade: {(m.monthlyQuota ?? (m.amount - m.inscriptionsAmount)).toFixed(2)} € + Inscrição: {m.inscriptionsAmount.toFixed(2)} €
                                                        </span>
                                                    )}
                                                </div>
                                                <span className="pms-month-amount">{m.amount.toFixed(2)} €</span>
                                            </label>
                                        );
                                    })}

                                    {/* ── Future months section ─────────────────── */}
                                    {allPayableMonths.some(m => m.isFuture) && (
                                        <>
                                            <div className="pms-section-divider">
                                                <span>Pagamento antecipado</span>
                                            </div>
                                            {allPayableMonths.filter(m => m.isFuture).map((m) => {
                                                const checked = selectedMonthKeys.has(m.key);
                                                const monthLabel = new Date(m.periodYear, m.periodMonth - 1, 1)
                                                    .toLocaleDateString('pt-PT', { month: 'long', year: 'numeric' });
                                                const label = monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1);
                                                return (
                                                    <label
                                                        key={m.key}
                                                        className={`pms-month-row ${checked ? 'checked' : ''} is-future`}
                                                    >
                                                        <div className="pms-checkbox-wrap">
                                                            <input type="checkbox" checked={checked}
                                                                onChange={() => toggleMonthSelection(m.key)}
                                                                className="pms-checkbox" />
                                                            <span className="pms-custom-check">{checked && <FaCheck />}</span>
                                                        </div>
                                                        <div className="pms-month-info">
                                                            <div className="pms-month-title-row">
                                                                <span className="pms-month-name">{label}</span>
                                                                <div className="pms-badges-wrap">
                                                                    <span className="pms-badge future">Antecipado</span>
                                                                    {m.inscriptionsAmount > 0 && (
                                                                        <span className="pms-badge inscription">
                                                                            + Inscrição {m.inscriptions?.map(i => i.sportName).join(', ')}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            {m.inscriptionsAmount > 0 && (
                                                                <span className="pms-month-subdetail">
                                                                    Mensalidade: {(m.monthlyQuota ?? (m.amount - m.inscriptionsAmount)).toFixed(2)} € + Inscrição: {m.inscriptionsAmount.toFixed(2)} €
                                                                </span>
                                                            )}
                                                        </div>
                                                        <span className="pms-month-amount">{m.amount.toFixed(2)} €</span>
                                                    </label>
                                                );
                                            })}
                                        </>
                                    )}
                                </div>

                                <div className="pms-footer">
                                    <div className="pms-total-row">
                                        <span className="pms-total-label">
                                            Total selecionado
                                            <span className="pms-total-count">
                                                ({selectedMonthKeys.size} {selectedMonthKeys.size === 1 ? 'mês' : 'meses'})
                                            </span>
                                        </span>
                                        <span className="pms-total-value">{selectedTotal.toFixed(2)} €</span>
                                    </div>

                                    {(() => {
                                        const nonFutureCount = allPayableMonths.filter(m => !m.isFuture).length;
                                        const selectedNonFutureCount = allPayableMonths
                                            .filter(m => !m.isFuture && selectedMonthKeys.has(m.key)).length;
                                        const unselectedDebt = nonFutureCount - selectedNonFutureCount;
                                        if (unselectedDebt <= 0) return null;
                                        return (
                                            <p className="pms-remaining-note">
                                                ⚠️ {unselectedDebt} {unselectedDebt === 1 ? 'mês ficará' : 'meses ficarão'} em dívida
                                            </p>
                                        );
                                    })()}

                                    <div className="pms-actions">
                                        <button
                                            className="pms-btn-cancel"
                                            onClick={() => setShowPaymentModal(false)}
                                        >Cancelar</button>
                                        <button
                                            className="pms-btn-confirm"
                                            onClick={handleConfirmPayment}
                                            disabled={selectedMonthKeys.size === 0}
                                        >
                                            <FaExternalLinkAlt />
                                            Pagar {selectedTotal.toFixed(2)} €
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="payment-extra-info">
                    <InscriptionAlert inscriptionInfo={inscriptionInfo} />
                </div>

                <div className="payment-history-section">
                    <div className="history-divider"></div>
                    <div
                        className="history-header"
                        onClick={() => setIsHistoryOpen(!isHistoryOpen)}
                    >
                        <h4 className="history-title">
                            <FaReceipt className="history-icon" /> Histórico de Pagamentos
                            {isHistoryOpen ? <FaChevronUp className="chevron" /> : <FaChevronDown className="chevron" />}
                        </h4>

                        {isHistoryOpen && (
                            <select
                                value={historyYear}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => onHistoryYearChange(parseInt(e.target.value))}
                                className="history-year-select"
                            >
                                {(() => {
                                    const cYear = new Date().getFullYear();
                                    const availableYears = Array.from(new Set([
                                        historyYear,
                                        cYear + 1,
                                        cYear,
                                        cYear - 1,
                                        cYear - 2
                                    ])).filter(Boolean).sort((a, b) => b - a);
                                    return availableYears.map(y => <option key={y} value={y}>{y}</option>);
                                })()}
                            </select>
                        )}
                    </div>

                    {isHistoryOpen && (
                        <div className="history-list">
                            {mergedHistory.length === 0 ? (
                                <p className="no-history">
                                    Nenhum pagamento efetuado em {historyYear}.
                                </p>
                            ) : (
                                mergedHistory.map((p, idx) => (
                                    <div key={idx} className="history-item">
                                        <div className="history-main-box">
                                            <div className="history-info-row">
                                                <div className={`status-dot ${p.displayStatus.toLowerCase()}`}>
                                                    {p.displayStatus === 'Completed' ? <FaCheck /> : p.displayStatus === 'Pending' ? <FaClock /> : <FaExclamationCircle />}
                                                </div>
                                                <span className={`payment-desc ${p.displayStatus === 'Unpaid' ? 'unpaid' : ''}`}>
                                                    {(() => {
                                                        const isAnnualPay = p.type === 'quota' && (!p.periodMonth || p.description?.includes("Anual"));
                                                        if (isAnnualPay) {
                                                            if (p.periodMonth && p.periodYear) {
                                                                try {
                                                                    const startMonth = p.periodMonth;
                                                                    const startYear = p.periodYear;
                                                                    const endMonth = startMonth === 1 ? 12 : startMonth - 1;
                                                                    const endYear = startMonth === 1 ? startYear : startYear + 1;
                                                                    return `Quota Anual (${MONTHS_PT[startMonth - 1]} ${startYear} - ${MONTHS_PT[endMonth - 1]} ${endYear})`;
                                                                } catch (e) {
                                                                    // fallback below
                                                                }
                                                            }
                                                            const payDate = p.paymentDate;
                                                            if (payDate) {
                                                                try {
                                                                    const start = new Date(payDate);
                                                                    const end = new Date(start);
                                                                    end.setFullYear(start.getFullYear() + 1);
                                                                    return `Quota Anual (${MONTHS_PT[start.getMonth()]} ${start.getFullYear()} - ${MONTHS_PT[end.getMonth()]} ${end.getFullYear()})`;
                                                                } catch (e) {
                                                                    // fallback below
                                                                }
                                                            }
                                                            return `Quota Anual ${p.periodYear}`;
                                                        }
                                                        return p.type === 'quota' && p.periodMonth
                                                            ? `${MONTHS_PT[p.periodMonth - 1]} ${p.periodYear}`
                                                            : p.description;
                                                    })()}
                                                </span>
                                                {p.type === 'inscription' && <span className="item-tag blue">Inscrição</span>}
                                                {p.displayStatus === 'Pending' && <span className="item-tag gold">Pendente</span>}
                                                {p.displayStatus === 'Unpaid' && <span className="item-tag red">Não Pago</span>}
                                            </div>
                                            <span className="payment-date-info">
                                                {p.displayStatus === 'Completed'
                                                    ? `Pago em ${new Date(p.paymentDate).toLocaleDateString('pt-PT')}`
                                                    : p.displayStatus === 'Pending' ? 'A aguardar pagamento' : 'Pagamento em falta'}
                                            </span>
                                        </div>
                                        <div className="history-amount-box">
                                            <strong className={p.displayStatus === 'Unpaid' ? 'unpaid' : ''}>
                                                {p.amount.toFixed(2)} €
                                            </strong>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PaymentCard;
